/*
"Comparer deux cartes" — side-by-side diff of two CardContent rows
the user has access to. Mirrors the Android CardCompareActivity:
section-by-section walk through SELECT / SV / Environment /
Contracts / Events, pairing fields by their technical `name` and
highlighting rows where the value differs.

Reached via /cards/compare?a=<idA>&b=<idB> — the IDs come from the
"Comparer…" picker on CardDetailPage. Both cards are fetched via
the existing GET /api/contents/:id; access control falls out of the
server-side canReadCard gate (owner / admin / active sharee).
*/

import { useEffect, useState, type JSX } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";

import { ApiError, api } from "../lib/api";
import { describeSelectField } from "../lib/startupInfoLookup";
import { ErrorState } from "../components/ErrorState";
import { Skeleton } from "../components/Skeleton";

interface DecodedField
{
	name:	string;
	label:	string;
	raw:	string;
	value:	string;
	status:	string;
}

interface DecodedBlock
{
	index?:	number;
	fields:	DecodedField[];
}

interface DecodedView
{
	select?:		DecodedField[];
	sv?:			DecodedField[];
	environment?:	DecodedField[];
	contracts?:		DecodedBlock[];
	events?:		DecodedBlock[];
}

interface CardDetail
{
	id:				string;
	serialNumber:	string;
	cardType:		string;
	dataModel:		string;
	readDate:		number;
	folderPath:		string;
	decoded:		DecodedView | null;
}

export function CompareCardsPage(): JSX.Element
{
	const { t }		= useTranslation("compare");
	const { t: tc }	= useTranslation("common");
	const [vSearchParams] = useSearchParams();

	const vIdA = vSearchParams.get("a") ?? "";
	const vIdB = vSearchParams.get("b") ?? "";

	const [fCardA,	fSetCardA]	= useState<CardDetail | null>(null);
	const [fCardB,	fSetCardB]	= useState<CardDetail | null>(null);
	const [fError,	fSetError]	= useState<string | null>(null);

	useEffect(() =>
	{
		if (vIdA === "" || vIdB === "")
		{
			fSetError(t("missing_ids"));
			return;
		}

		Promise.all(
			[
				api<CardDetail>("GET", `/contents/${encodeURIComponent(vIdA)}`),
				api<CardDetail>("GET", `/contents/${encodeURIComponent(vIdB)}`),
			]
		)
			.then(([vA, vB]) => { fSetCardA(vA); fSetCardB(vB); })
			.catch((vErr: unknown) =>
			{
				fSetError(vErr instanceof ApiError ? vErr.message : tc("error_network"));
			});
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [vIdA, vIdB]);

	if (fError !== null)
	{
		return <ErrorState message={fError} />;
	}

	if (fCardA === null || fCardB === null)
	{
		return (
			<div className="card">
				<Skeleton variant="title" widthPercent={45} />
				<Skeleton variant="row" widthPercent={80} />
				<Skeleton variant="row" widthPercent={75} />
				<Skeleton variant="row" widthPercent={85} />
			</div>
		);
	}

	const vDecodedA = fCardA.decoded ?? {};
	const vDecodedB = fCardB.decoded ?? {};

	return (
		<>
			<div className="card">
				<h2>{t("title")}</h2>
				<table className="compare-header">
					<tbody>
						<tr>
							<th></th>
							<th><Link to={`/cards/${fCardA.id}`}><code>{fCardA.serialNumber}</code></Link></th>
							<th><Link to={`/cards/${fCardB.id}`}><code>{fCardB.serialNumber}</code></Link></th>
						</tr>
						<tr>
							<th>{t("card_type")}</th>
							<DiffCell a={fCardA.cardType}  b={fCardB.cardType} />
						</tr>
						<tr>
							<th>{t("data_model")}</th>
							<DiffCell a={fCardA.dataModel} b={fCardB.dataModel} />
						</tr>
						<tr>
							<th>{t("read_date")}</th>
							<DiffCell a={formatDateTime(fCardA.readDate)} b={formatDateTime(fCardB.readDate)} />
						</tr>
						<tr>
							<th>{t("folder")}</th>
							<DiffCell a={fCardA.folderPath} b={fCardB.folderPath} />
						</tr>
					</tbody>
				</table>
			</div>

			<CompareSection title={t("section_select")}      fieldsA={decorateSelectFields(vDecodedA.select)} fieldsB={decorateSelectFields(vDecodedB.select)} />
			<CompareSection title={t("section_sv")}          fieldsA={vDecodedA.sv}     fieldsB={vDecodedB.sv} />
			<CompareSection title={t("section_environment")} fieldsA={vDecodedA.environment} fieldsB={vDecodedB.environment} />

			<CompareBlocks title={t("section_contracts")} blocksA={vDecodedA.contracts} blocksB={vDecodedB.contracts} />
			<CompareBlocks title={t("section_events")}    blocksA={vDecodedA.events}    blocksB={vDecodedB.events} />
		</>
	);
}

/*
A single value cell in the header table. When the two values
disagree, both cells get a soft amber highlight so the eye can
spot the diff without scanning every row.
*/
function DiffCell(
	props: { a: string; b: string }
): JSX.Element
{
	const vDiff	= props.a !== props.b;	// True when the values disagree
	const vCls	= vDiff ? "compare-diff" : "";

	return (
		<>
			<td className={vCls}>{props.a}</td>
			<td className={vCls}>{props.b}</td>
		</>
	);
}

/*
One section of decoded fields (SELECT / SV / Environment).
Pairs A.fields and B.fields by their technical `name`. Fields
present on only one side are still shown with the other column
muted, so the "this card has it but the other doesn't" case is
visible.
*/
function CompareSection(
	props: { title: string; fieldsA: DecodedField[] | undefined; fieldsB: DecodedField[] | undefined }
): JSX.Element
{
	const { t } = useTranslation("compare");

	const vA = props.fieldsA ?? [];
	const vB = props.fieldsB ?? [];

	if (vA.length === 0 && vB.length === 0)
	{
		return <></>;
	}

	const vPairs = pairFieldsByName(vA, vB);

	return (
		<div className="card">
			<h2>{props.title}</h2>
			<table className="compare-table">
				<thead>
					<tr>
						<th>{t("field")}</th>
						<th>{t("value_a")}</th>
						<th>{t("value_b")}</th>
					</tr>
				</thead>
				<tbody>
					{vPairs.map((p, i) => (
						<tr key={i}>
							<td className="muted">{p.label}</td>
							<DiffCell a={p.valueA} b={p.valueB} />
						</tr>
					))}
				</tbody>
			</table>
		</div>
	);
}

/*
Repeated-block sections (contracts, events). Pairs by index — the
Nth contract on A is compared with the Nth contract on B. Mirrors
how Android's CardCompareActivity walks the lists.
*/
function CompareBlocks(
	props: { title: string; blocksA: DecodedBlock[] | undefined; blocksB: DecodedBlock[] | undefined }
): JSX.Element
{
	const vA = props.blocksA ?? [];
	const vB = props.blocksB ?? [];

	if (vA.length === 0 && vB.length === 0)
	{
		return <></>;
	}

	const vCount = Math.max(vA.length, vB.length);
	const vBlocks: JSX.Element[] = [];

	for (let i = 0; i < vCount; i++)
	{
		const vBlockA = vA[i];
		const vBlockB = vB[i];
		const vTitle  = `${props.title} #${i + 1}`;

		vBlocks.push(
			<CompareSection
				key={i}
				title={vTitle}
				fieldsA={vBlockA?.fields}
				fieldsB={vBlockB?.fields}
			/>
		);
	}

	return <>{vBlocks}</>;
}

/*
Pairs A.fields and B.fields by `name` and produces a stable list
of {label, valueA, valueB} rows. Labels prefer A's; if a name only
exists in B, the B label is used.
*/
function pairFieldsByName(
	inA:	DecodedField[],
	inB:	DecodedField[]
): Array<{ label: string; valueA: string; valueB: string }>
{
	const vMapB = new Map<string, DecodedField>();	// name → B field

	for (const v of inB) { vMapB.set(v.name, v); }

	const vSeen	= new Set<string>();
	const vRows: Array<{ label: string; valueA: string; valueB: string }> = [];

	// First pass: A in document order, paired with B if present.
	for (const vA of inA)
	{
		const vB = vMapB.get(vA.name);

		vRows.push(
			{
				label:	vA.label,
				valueA:	vA.value,
				valueB:	vB !== undefined ? vB.value : "",
			}
		);

		vSeen.add(vA.name);
	}

	// Second pass: B-only entries appended at the end.
	for (const vB of inB)
	{
		if (vSeen.has(vB.name))
		{
			continue;
		}

		vRows.push({ label: vB.label, valueA: "", valueB: vB.value });
	}

	return vRows;
}

/*
Replaces the `value` of every Platform / ApplicationType /
SoftwareIssuer field in a SELECT field list with the human-readable
descriptor from CalypsoTN001. Returns undefined when the input is
undefined so callers can pass through whatever shape they already
received.
*/
function decorateSelectFields(
	inFields:	DecodedField[] | undefined		// SELECT fields from one card
): DecodedField[] | undefined
{
	if (inFields === undefined)
	{
		return undefined;
	}

	return inFields.map((vField) => (
		{
			...vField,
			value:	describeSelectField(vField.name, vField.raw, vField.value),
		}
	));
}

/*
Formats ms-since-epoch to "YYYY-MM-DD HH:MM" in the local timezone.
*/
function formatDateTime(
	inMs:	number
): string
{
	const vDate = new Date(inMs);
	const pad   = (n: number) => String(n).padStart(2, "0");

	return `${vDate.getFullYear()}-${pad(vDate.getMonth() + 1)}-${pad(vDate.getDate())} ${pad(vDate.getHours())}:${pad(vDate.getMinutes())}`;
}
